<template>
  <v-dialog v-model="isActive" fullscreen hide-overlay transition="dialog-bottom-transition" class="container" scrollable>
    <v-card class="content">
      <div class="header">
        <div>
          <h2>Pesquisar produto</h2>
          <div>Atrelar produto ao orçamento selecionado</div>
        </div>
        <div class="actions">
          <div @click="handleAddProduct()" class="add">
            <v-icon color="primary">mdi-plus</v-icon>
          </div>
          <div @click="close()" class="return">
            <v-icon color="white">mdi-close</v-icon>
          </div>
        </div>
      </div>
      <div class="search">
        <v-text-field v-model="term" v-debounce:1s="search" label="Pesquisar produto" outlined hide-details clearable></v-text-field>
      </div>
      <div class="list-container">
        <v-list-item-group class="product-list">
          <LazyList :items="filteredProducts" @load-more="loadMoreData" :has-more-data="hasMoreData">
            <template v-slot:default="{ items }">
              <div v-for="product in items" :key="product.id" style="height: 86px; display: flex;">
                <v-list-item @click="updateSelectedProducts(product.id, !selectedProductIds.includes(product.id))">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>{{ product.description }}</v-list-item-title>
                      <v-list-item-subtitle>{{ formatCurrency(product.value) }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn class="add-btn" icon>
                        <v-icon color="green" small>mdi-plus</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </div>
            </template>
          </LazyList>

          <div class="loader" v-if="isLoading">
            <v-row v-for="index in 15" :key="index">
              <v-col cols="12">
                <v-skeleton-loader type="list-item-two-line"></v-skeleton-loader>
              </v-col>
            </v-row>
          </div>

          <div v-if="filteredProducts.length === 0" class="no-results">Nenhum produto encontrado</div>
        </v-list-item-group>

        <div v-if="selectedProducts.length !== 0" class="selected-products">
          <span class="label">Produtos selecionados:</span>
          <v-simple-table
            fixed-header
            height="300px"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    Obrigatório
                  </th>
                  <th class="text-left">
                    Descrição/Valor
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="product in selectedProducts"
                  :key="product.id"
                >
                  <td>
                    <v-switch
                      v-model="product.required"
                      color="primary"
                    ></v-switch>
                  </td>
                  <td>
                    <div>
                      <span>{{ product.description }}</span>
                    </div>
                    <div>
                      <span>{{ formatCurrency(product.value) }}</span>
                    </div>
                  </td>
                  <td>
                    <v-btn icon @click="updateSelectedProducts(product.id, false)">
                      <v-icon color="red" small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        <div class="set-filters-action">
          <v-btn block color="primary" bottom :disabled="selectedProductCount === 0" @click="addSelectedProducts">
            Adicionar {{ selectedProductCount }} produto(s)
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-bottom-sheet v-model="isAddProduct" persistent>
      <div class="bottom-sheet-content">
        <div class="bottom-sheet-header">
          <span class="bottom-sheet-title">Novo produto</span>
          <v-btn icon @click="closeManageModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-text-field v-model="product.code" label="Código" outlined></v-text-field>
        <v-text-field v-model="product.description" label="Nome" outlined></v-text-field>
        <v-text-field v-model="product.value" ref="value" label="Valor" outlined v-money="valor"></v-text-field>
        <v-text-field v-model="product.cost_value" ref="costValue" label="Valor de custo" outlined v-money="valor"></v-text-field>
        <v-text-field v-model="product.commission" ref="commission" label="Comissão" outlined v-money="percentual"></v-text-field>
        <v-btn color="primary" block @click="addProduct" :disabled="!product.description || !product.value">Salvar</v-btn>
      </div>
    </v-bottom-sheet>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';

import LazyList from "@/components/Common/LazyList";
import ProductService from '@/services/api/product';

export default {
  name: 'ProductSelector',
  emits: ['close', 'select'],
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    LazyList,
  },
  data() {
    return {
      term: '',
      products: [],
      selectedProductIds: [],
      selectedProducts: [],
      currentPage: 1,
      itemsPerPage: 15,
      isLoading: false,
      hasMoreData: false,
      isAddProduct: false,
      product: {
        code: '',
        description: null,
        value: '0',
        cost_value: null,
        commission: null
      },
      valor: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: true
      },
      percentual: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: ' %',
        precision: 2,
        masked: true
      }
    };
  },
  watch: {
    term() {
      this.currentPage = 1;
      this.products = [];
    },
    product: {
      handler(newValue) {
        if (newValue.commission === null) {
          return;
        }

        const commission = parseFloat(newValue.commission.replace('%', '').replace('.', '').replace(',', '.').trim());

        if (commission > 100) {
          this.commission = '100.00';
          this.$refs.commission.$el.getElementsByTagName('input')[0].value = this.commission;
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState('Budget', ['selectedBudget']),
    filteredProducts() {
      if (!this.products) {
        return [];
      }

      return this.products.filter((product) => !this.selectedProductIds.includes(product.id));
    },
    selectedProductCount() {
      return this.selectedProductIds.length;
    },
  },
  methods: {
    close() {
      this.term = '';
      this.selectedProductIds = [];
      this.selectedProducts = [];
      this.products = [];

      this.$emit('close');
    },
    addSelectedProducts() {
      this.$emit('select', this.selectedProducts);
      this.close();
    },
    formatCurrency(value) {
      return value ? parseFloat(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
    },
    async search() {
      if (!this.term || this.term.trim() === '' || this.term.length < 3) {
        this.products = [];
        return;
      }

      this.isLoading = true;

      const queryParams = {
        description: this.term,
        size: this.itemsPerPage,
        page: this.currentPage,
        budget_id: this.selectedBudget.id,
      };

      try {
        const response = await ProductService.search(queryParams);
        const responseData = response.data;

        if (responseData && responseData.rows) {
          const newProducts = responseData.rows.filter(product => !this.products.find(s => s.id === product.id));
          this.products = [...this.products, ...newProducts];
        }

        this.currentPage = responseData.currentPage || 1;
        const totalPages = responseData.totalPages || 1;
        this.hasMoreData = this.currentPage < totalPages;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    updateSelectedProducts(productId, value) {
      if (value) {
        if (!this.selectedProductIds.includes(productId)) {
          const selectedProduct = this.products.find(product => product.id === productId);
          if (selectedProduct) {
            selectedProduct.required = true;
            this.selectedProductIds.push(productId);
            this.selectedProducts.push(selectedProduct);
          }
        }
      } else {
        this.selectedProductIds = this.selectedProductIds.filter(id => id !== productId);
        this.selectedProducts = this.selectedProducts.filter(product => product.id !== productId);
      }
    },
    loadMoreData() {
      if (this.hasMoreData && !this.isLoading) {
        this.currentPage += 1;
        this.search();
      }
    },
    handleAddProduct() {
      this.isAddProduct = true;
    },
    addProduct() {
      this.isAddProduct = false;
      this.isLoading = true;

      ProductService.create({
        code: this.product.code,
        description: this.product.description,
        value: parseFloat(this.product.value.replace('R$ ', '').replace('.', '').replace(',', '.').trim()),
        cost_value: parseFloat(this.product.cost_value.replace('R$ ', '').replace('.', '').replace(',', '.').trim()),
        commission: parseFloat(this.product.commission.replace('%', '').replace('.', '').replace(',', '.').trim()),
      })
        .then((response) => {
          this.products = [response.data, ...this.products];
          this.cleanProduct();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    closeManageModal() {
      this.isAddProduct = false;
    },
    cleanProduct() {
      this.product = {
        code: '',
        description: null,
        value: '0',
        cost_value: null,
        commission: null
      };

      this.$refs.value.$el.getElementsByTagName('input')[0].value = '0.00';
      this.$refs.commission.$el.getElementsByTagName('input')[0].value = '0.00';
    }
  }
};
</script>

<style lang="scss" scoped>
.content {
  flex: 1;
  display: flex;
  flex-direction: column;

  .header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    background-color: #3e515f;
    padding: 20px 20px;
    color: #fff;
    z-index: 1;
    border-radius: 0px !important;

    .actions {
      display: flex;
      gap: 16px;
      justify-content: flex-end;
      align-items: center;

      .add,
      .return {
        border-radius: 50%;
        padding: 10px;
      }

      .add {
        background-color: white;
      }

      .return {
        background-color: #2d3c47;
      }
    }
  }

  .search {
    background-color: #f5f5f5;
    padding: 20px;
  }

  .list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .product-list {
      flex: 1;
      max-height: 250px;
      overflow-y: auto;
      overflow-x: hidden;

      .add-btn {
        min-width: 0;
        padding: 5px;
        border-radius: 50%;
        border: 1px solid #ccc;
        color: #48b38b !important;
      }

      .no-results {
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
        text-align: center;
        margin-top: 50px;
      }
    }

    .selected-products {
      padding: 16px;

      .label {
        font-weight: bold;
        margin-right: 8px;
      }
    }

    .set-filters-action {
      padding: 0 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #fff;
      border-top: 1px solid rgba(0, 0, 0, 0.12);
      margin-bottom: 12px;
      margin-top: 12px;
      padding-top: 12px;
    }
  }
}

.v-btn--icon {
  margin: 0 !important;
}

.v-btn--icon.v-size--default {
  height: 24px !important;
  width: 24px !important;
}

.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none !important;
}

.bottom-sheet-content {
  padding: 16px;

  .bottom-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .bottom-sheet-title {
      font-size: 18px;
      font-weight: 500;
    }
  }
}
</style>
